import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, ArrowRight, Trophy, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [nextMatch, setNextMatch] = useState(null);
  const [lastMatch, setLastMatch] = useState(null);
  const [news, setNews] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [nextRes, lastRes, newsRes, sponsorsRes] = await Promise.all([
        axios.get(`${API}/matches/next`).catch(() => ({ data: null })),
        axios.get(`${API}/matches/last`).catch(() => ({ data: null })),
        axios.get(`${API}/news?limit=3`).catch(() => ({ data: [] })),
        axios.get(`${API}/sponsors`).catch(() => ({ data: [] })),
      ]);
      setNextMatch(nextRes.data);
      setLastMatch(lastRes.data);
      setNews(newsRes.data);
      setSponsors(sponsorsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-PT', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&q=80')`,
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 bg-black/50" />
        
        <div className="relative z-10 text-center section-padding">
          <div className="animate-fade-in-up">
            <div className="w-24 h-24 mx-auto mb-8 bg-white rounded-full flex items-center justify-center">
              <span className="font-heading font-black text-black text-3xl">GDR</span>
            </div>
            <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter mb-4">
              GDR ESPORÕES
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-2 font-light">
              Grupo Desportivo e Recreativo de Esporões
            </p>
            <p className="text-gray-400 mb-8">Desde 1979 • Esporões, Braga</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/jogos" className="btn-primary" data-testid="hero-calendar-btn">
                VER CALENDÁRIO
              </Link>
              <Link to="/clube" className="btn-secondary" data-testid="hero-club-btn">
                CONHECER O CLUBE
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white rounded-full" />
          </div>
        </div>
      </section>

      {/* Match Center */}
      <section className="bg-[#0A0A0A] border-y border-white/10" data-testid="match-center">
        <div className="container-custom section-padding py-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Next Match */}
            <div className="match-card" data-testid="next-match-card">
              <div className="flex items-center gap-2 mb-4">
                <div className="live-dot" />
                <span className="text-xs uppercase tracking-widest text-green-500 font-bold">Próximo Jogo</span>
              </div>
              {nextMatch ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-center flex-1">
                      <div className="w-16 h-16 mx-auto mb-2 bg-white rounded-full flex items-center justify-center">
                        <span className="font-heading font-black text-black">GDR</span>
                      </div>
                      <p className="font-heading font-bold text-sm">GDR ESPORÕES</p>
                    </div>
                    <div className="px-4">
                      <span className="font-mono text-2xl text-gray-500">VS</span>
                    </div>
                    <div className="text-center flex-1">
                      <div className="w-16 h-16 mx-auto mb-2 bg-white/10 rounded-full flex items-center justify-center">
                        <span className="font-heading font-bold text-gray-400">?</span>
                      </div>
                      <p className="font-heading font-bold text-sm">{nextMatch.opponent}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-6 text-sm text-gray-400">
                    <div className="flex items-center gap-2">
                      <Calendar size={14} />
                      <span>{formatDate(nextMatch.date)} • {nextMatch.time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin size={14} />
                      <span>{nextMatch.location}</span>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-gray-500 text-center py-8">Nenhum jogo agendado</p>
              )}
            </div>

            {/* Last Result */}
            <div className="match-card" data-testid="last-match-card">
              <div className="flex items-center gap-2 mb-4">
                <Trophy size={14} className="text-gray-400" />
                <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Último Resultado</span>
              </div>
              {lastMatch ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-center flex-1">
                      <div className="w-16 h-16 mx-auto mb-2 bg-white rounded-full flex items-center justify-center">
                        <span className="font-heading font-black text-black">GDR</span>
                      </div>
                      <p className="font-heading font-bold text-sm">GDR ESPORÕES</p>
                    </div>
                    <div className="px-4">
                      <div className="score-display">
                        <span className={lastMatch.home_away === 'home' ? '' : ''}>{lastMatch.home_away === 'home' ? lastMatch.home_score : lastMatch.away_score}</span>
                        <span className="text-gray-600 mx-2">-</span>
                        <span>{lastMatch.home_away === 'home' ? lastMatch.away_score : lastMatch.home_score}</span>
                      </div>
                    </div>
                    <div className="text-center flex-1">
                      <div className="w-16 h-16 mx-auto mb-2 bg-white/10 rounded-full flex items-center justify-center">
                        <span className="font-heading font-bold text-gray-400">?</span>
                      </div>
                      <p className="font-heading font-bold text-sm">{lastMatch.opponent}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-6 text-sm text-gray-400">
                    <span>{formatDate(lastMatch.date)}</span>
                    <span>{lastMatch.competition}</span>
                  </div>
                </>
              ) : (
                <p className="text-gray-500 text-center py-8">Nenhum resultado disponível</p>
              )}
            </div>
          </div>

          <div className="flex justify-center mt-8">
            <Link to="/jogos" className="btn-secondary flex items-center gap-2" data-testid="view-all-matches">
              VER TODOS OS JOGOS <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 section-padding" data-testid="about-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in-up">
              <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">O Clube</span>
              <h2 className="font-heading text-4xl md:text-5xl font-black mt-2 mb-6">
                UM CLUBE COM HISTÓRIA E PAIXÃO
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                Fundado a 27 de novembro de 1979, o Grupo Desportivo e Recreativo de Esporões é um símbolo de orgulho e determinação. Após um renascimento há quase duas décadas, o clube voltou à ribalta com força total.
              </p>
              <p className="text-gray-400 leading-relaxed mb-8">
                Atualmente disputa a 1ª Divisão da Associação de Futebol de Braga e lançou o projeto de formação "Gverreiros do Futuro de Esporões" em parceria com o SC Braga.
              </p>
              <Link to="/clube" className="btn-primary" data-testid="learn-more-club">
                SABER MAIS
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-square bg-[#0A0A0A] border border-white/10 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?auto=format&fit=crop&q=80"
                  alt="GDR Esporões"
                  className="w-full h-full object-cover img-grayscale"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 bg-white text-black p-8">
                <p className="font-heading text-6xl font-black">1979</p>
                <p className="text-sm uppercase tracking-widest">Fundação</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0A0A0A] border-y border-white/10" data-testid="stats-section">
        <div className="container-custom section-padding">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '1979', label: 'Fundação' },
              { number: '45+', label: 'Anos de História' },
              { number: '80+', label: 'Jovens na Formação' },
              { number: '1ª', label: 'Divisão AF Braga' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <p className="stat-number">{stat.number}</p>
                <p className="text-gray-400 text-sm uppercase tracking-widest mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-24 section-padding" data-testid="news-section">
        <div className="container-custom">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Atualidade</span>
              <h2 className="font-heading text-4xl md:text-5xl font-black mt-2">NOTÍCIAS</h2>
            </div>
            <Link to="/noticias" className="hidden md:flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <span className="text-xs uppercase tracking-widest font-bold">Ver Todas</span>
              <ChevronRight size={16} />
            </Link>
          </div>

          {news.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {news.map((item, index) => (
                <Link
                  key={item.id}
                  to={`/noticias/${item.id}`}
                  className="news-card group"
                  data-testid={`news-card-${index}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={item.image_url || 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&q=80'}
                      alt={item.title}
                      className="news-image w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-xs uppercase tracking-widest text-gray-500">
                      {new Date(item.created_at).toLocaleDateString('pt-PT')}
                    </span>
                    <h3 className="font-heading text-xl font-bold mt-2 group-hover:text-gray-300 transition-colors line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 text-sm mt-2 line-clamp-2">{item.content}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 border border-white/10">
              <p className="text-gray-500">Nenhuma notícia disponível</p>
            </div>
          )}

          <div className="mt-8 md:hidden">
            <Link to="/noticias" className="btn-secondary w-full flex items-center justify-center gap-2">
              VER TODAS AS NOTÍCIAS <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Sponsors Section */}
      {sponsors.length > 0 && (
        <section className="py-16 bg-[#0A0A0A] border-y border-white/10" data-testid="sponsors-section">
          <div className="container-custom section-padding">
            <div className="text-center mb-12">
              <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Parceiros</span>
              <h2 className="font-heading text-3xl font-black mt-2">PATROCINADORES</h2>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-12">
              {sponsors.slice(0, 6).map((sponsor) => (
                <a
                  key={sponsor.id}
                  href={sponsor.website || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sponsor-logo"
                  data-testid={`sponsor-${sponsor.id}`}
                >
                  <img
                    src={sponsor.logo_url}
                    alt={sponsor.name}
                    className="h-12 w-auto object-contain"
                  />
                </a>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/patrocinadores" className="text-gray-400 hover:text-white text-xs uppercase tracking-widest font-bold transition-colors">
                Ver Todos os Patrocinadores →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 section-padding" data-testid="cta-section">
        <div className="container-custom">
          <div className="bg-white text-black p-12 md:p-16 text-center">
            <h2 className="font-heading text-4xl md:text-5xl font-black mb-4">
              JUNTA-TE À FAMÍLIA ESPORÕES
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Torna-te sócio e apoia o teu clube. Juntos somos mais fortes!
            </p>
            <Link
              to="/contactos"
              className="inline-block bg-black text-white px-8 py-4 uppercase font-bold tracking-widest text-sm hover:bg-gray-900 transition-colors"
              data-testid="cta-contact"
            >
              CONTACTAR O CLUBE
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
